import React, { useEffect, useState, useCallback } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { applicationActions } from '@apply/adminStore';
import { ApplicationApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { RefreshEvents } from '../../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { ModalSummaryHeader } from '../ApplicationComponents';
import {
    handleAdminError,
    handleAdminSuccess,
    addressTooltipText,
    borrowerTooltipText,
    handleAdminWarning,
} from '../../../common/SharedFunctions';
import List from 'devextreme-react/list';
import debounce from 'lodash.debounce';
import { BfiDataGrid, Column, MasterDetail, BfiCustomStore } from '@apply/ui';
import { getLoanTypeShortName, onRowPrepared } from '../../../loans/common/LoanFunctions';
import { formatDateMonthDayYear } from '@apply/ui';
import { ApplicationStatusTypeOptions } from '@apply/constants';
import { Tooltip } from 'devextreme-react/tooltip';
import ApplicationPortfolioTable from '../../../borrowers/tables/ApplicationPortfolioTable';
import { ConfirmModal } from '@apply/ui';
import { BfiNumber } from '../../../common/SharedComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';

export const MoveType = {
    Delete: 0,
    NewApplication: 1,
    ExistingApplication: 2,
};

const MovePropertiesModal = () => {
    const { isOpen, id, propertyId = null } = useSelector((state) => state.application.movePropertiesModal);
    const { dispatch } = store;

    const [application, set_application] = useState(null);
    const [properties, set_properties] = useState([]);
    const [targetAppId, set_targetAppId] = useState(0);
    const [bfiNumber, set_bfiNumber] = useState('');
    const [moveType, set_moveType] = useState(MoveType.NewApplication);
    const [searchText, set_searchText] = useState('');

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const gridStore = new BfiCustomStore();
    const [dataStore, set_dataStore] = useState(undefined);

    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const [confirmModalOpen, set_confirmModalOpen] = useState(false);

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        formRow: { marginBottom: '0' },
        buttonContainer: {
            marginRight: '10px',
            marginLeft: '10px',
            textAlign: 'right',
        },
        disabled: { color: '#e6e6e6' },
        enabled: { color: 'black' },
        optionDiv: { padding: '5px' },
        instructions: {
            fontWeight: 'bold',
            marginBottom: '7px',
            color: '#2c5591',
        },
        instructions2: {
            fontSize: 'smaller',
            marginBottom: '7px',
            color: 'black',
        },
        searchContainer: {
            padding: '0.5rem 0',
            height: '58px',
            borderBottom: '1px solid #cfcfe7',
        },
        searchBox: { paddingLeft: '30px' },
        propertyList: { border: '1px solid silver', borderRadius: '5px' },
    };

    const _handleClose = () =>
        dispatch(
            applicationActions.setMovePropertiesModal({
                movePropertiesModal: { isOpen: false },
            })
        );

    const dataFunction = (criteria) => {
        criteria.sourceAppId = id;
        return applicationApi.getMovePropertiesAppSearch(criteria);
    };

    const applySearch = (searchText) => {
        set_searchText(searchText);
        if (searchText) {
            gridStore.setOnLoad('id', dataFunction, searchText);
            set_dataStore(gridStore.store);
        }
    };

    const debouncedSearch = debounce(applySearch, 500);

    const loadApplication = async (id) => {
        const response = await applicationApi.getMoveProperties(id);
        if (response.statusCode !== 200) {
            set_loadingOpen(false);
            return;
        }

        const appl = response.result;
        set_application(appl);
        set_loadingOpen(false);
    };

    useEffect(() => {
        async function isOpenChanged() {
            resetState();

            if (!isOpen || !id) {
                return;
            }

            await loadApplication(id);
            set_loadingOpen(false);
        }

        isOpenChanged();
    }, [isOpen, id]);

    useEffect(() => {
        if (!application) {
            return;
        }

        if (propertyId) {
            const prop = application.properties.find((p) => p.id === propertyId);
            if (prop) {
                set_properties([prop]);
            }
        }

        if (isMoveTypeAvailable(MoveType.NewApplication)) {
            set_moveType(MoveType.NewApplication);
        } else if (isMoveTypeAvailable(MoveType.ExistingApplication)) {
            set_moveType(MoveType.ExistingApplication);
        } else if (isMoveTypeAvailable(MoveType.Delete)) {
            set_moveType(MoveType.Delete);
        }
    }, [application]);

    const canSubmit = () => {
        if (!application) {
            return false;
        }
        switch (moveType) {
            case MoveType.Delete: //Delete
                return properties.length > 0 && properties.length < application.properties.length; //Cannot delete all properties
            case MoveType.NewApplication: //Move to new app
                return bfiNumber && properties.length > 0 && properties.length < application.properties.length; //Cannot move all properties to a duplicate app
            case MoveType.ExistingApplication: //Move to existing app
                return properties.length > 0 && targetAppId;
        }

        let isOK = properties.length < application.properties.length;
        return isOK;
    };

    const submitChanges = async () => {
        switch (moveType) {
            case MoveType.Delete: //Delete
                await deleteProperties();
                break;
            case MoveType.NewApplication: //Move to new App
            case MoveType.ExistingApplication: //Move to existing app
                await moveProperties();
                break;
        }
    };

    const validateBfiNumbers = async () => {
        if (!bfiNumber) {
            return true;
        }

        set_loadingMessage('Validating BFI #...');
        set_loadingOpen(true);

        const model = {
            id: application.id,
            bfiNumber,
            isMasterNote: false,
        };
        var result = (await applicationApi.checkLoanAndBfiNumber(model)).result;
        let msg = result.message;
        if (result.isValid) {
            return true;
        } else {
            set_loadingOpen(false);
            handleAdminWarning(msg);
            return false;
        }
    };

    const deleteProperties = async () => {
        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                propertyIds: properties.map((a) => a.id),
            };

            const response = await applicationApi.setDeleteProperties(application.id, model);
            if (response.statusCode !== 200) {
                return;
            }

            refreshEvents.dispatchRefreshEvent();
            handleAdminSuccess('Changes saved successfully.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const moveProperties = async () => {
        try {
            const isOk = await validateBfiNumbers();
            if (!isOk) {
                return;
            }

            set_loadingMessage('Saving changes...');
            set_loadingOpen(true);

            const model = {
                sourceAppId: application.id,
                targetAppId: moveType === MoveType.NewApplication ? MoveType.Delete : targetAppId,
                bfiNumber,
                propertyIds: properties.map((a) => a.id),
            };

            const response = await applicationApi.setMovePropertiesAppSearch(application.id, model);
            if (response.statusCode !== 200) {
                return;
            }

            refreshEvents.dispatchRefreshEvent();
            handleAdminSuccess('Properties have been moved successfully.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const resetState = () => {
        set_application(null);
        set_properties([]);
        set_targetAppId(0);
        set_bfiNumber('');
        set_moveType(MoveType.NewApplication);
        set_searchText('');
    };

    const isMoveTypeAvailable = (moveType) => {
        switch (moveType) {
            case MoveType.Delete:
            case MoveType.NewApplication:
                return application.properties.length > 1;
            case MoveType.ExistingApplication:
                return application.properties.length > 0;
        }
    };

    const renderPropertyItem = (property) => {
        return (
            <div>
                {property.description && (
                    <div>
                        <b>
                            Property {property.bfiNumberSuffix} - {property.description}
                        </b>
                    </div>
                )}
                {property.address && (
                    <div
                        style={{
                            marginTop: '5px',
                            marginBottom: '10px',
                            fontWeight: 'bold',
                        }}
                    >
                        {property.address?.street1} {property.address?.street2} {property.address?.city},{' '}
                        {property.address?.state} {property.address?.zipCode}
                    </div>
                )}
            </div>
        );
    };

    const onPropertySelectionChanged = useCallback(
        ({ name, value }) => {
            if (name === 'selectedItemKeys') {
                set_properties(value);
            }
        },
        [properties]
    );

    const _onSearchChange = (event) => {
        debouncedSearch(event.target.value);
    };

    const onLoanTypeCellRender = (e) => {
        return getLoanTypeShortName(e.data.loanTypeId);
    };

    const onBorrowerCellRender = (e) => {
        const targetId = `be_${e.data.id}`;
        const text = borrowerTooltipText(e.data.borrower);
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.data.borrowerEntity}
                    {e.data.propertyCount > 1 && (
                        <span>
                            <b>&nbsp;[+{e.data.propertyCount}]</b>
                        </span>
                    )}
                </div>
                <Tooltip
                    target={`[data-for="${targetId}"]`}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    {text}
                </Tooltip>
            </>
        );
    };

    const onCityCellRender = (e) => {
        const targetId = `ac_${e.data.id}`;
        const text = addressTooltipText(e.data.address);
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.data.propertyCity}
                </div>
                {!e.data.isPortfolio && (
                    <Tooltip
                        target={`[data-for="${targetId}"]`}
                        showEvent='mouseenter'
                        hideEvent='mouseleave'
                        hideOnOutsideClick={false}
                    >
                        {text}
                    </Tooltip>
                )}
            </>
        );
    };

    const onStateCellRender = (e) => {
        const targetId = `as_${e.data.id}}`;
        const text = addressTooltipText(e.data.address);
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.data.propertyState}
                </div>
                {!e.data.isPortfolio && (
                    <Tooltip
                        target={`[data-for="${targetId}"]`}
                        showEvent='mouseenter'
                        hideEvent='mouseleave'
                        hideOnOutsideClick={false}
                    >
                        {text}
                    </Tooltip>
                )}
            </>
        );
    };

    const onDateRender = (e) => {
        if (!e.value) {
            return null;
        }
        return formatDateMonthDayYear(e.value);
    };

    const onAppStatusRender = (e) => {
        if (!e.value) {
            return null;
        }
        return ApplicationStatusTypeOptions.find((s) => s.id === e.value)?.shortName;
    };

    const renderPortfolioRecords = (e) => {
        if (!e.data.isPortfolio) {
            return null;
        }
        return <ApplicationPortfolioTable data={e.data.portfolio} />;
    };

    const onTargetAppSelected = (e) => {
        if (e.selectedRowKeys && e.selectedRowKeys.length > 0) {
            set_targetAppId(e.selectedRowKeys[0]);
        } else {
            set_targetAppId(0);
        }
    };

    const onConfirmAction = (e) => {
        e.preventDefault();
        e.stopPropagation();
        set_confirmModalOpen(true);
    };

    const onConfirmTrue = () => {
        set_confirmModalOpen(false);
        submitChanges();
    };

    const onConfirmFalse = () => {
        set_confirmModalOpen(false);
    };

    if (!isOpen || !application) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '1000px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form
                                    name='orderAppraisalForm'
                                    onSubmit={(event) => onConfirmAction(event)}
                                >
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1>Move Application Properties</h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={_handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>
                                    <ModalSummaryHeader application={application} />
                                    <div
                                        className='scrollable-content'
                                        style={{ margin: '10px' }}
                                    >
                                        <div className={'standard-form'}>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-12'>
                                                    <div style={styles.instructions}>1. Select Action...</div>
                                                    <div style={styles.optionDiv}>
                                                        <input
                                                            name='moveType'
                                                            id='moveType1'
                                                            value='1'
                                                            checked={moveType === MoveType.NewApplication}
                                                            type='radio'
                                                            onChange={(e) => set_moveType(parseInt(e.target.value))}
                                                            disabled={!isMoveTypeAvailable(MoveType.NewApplication)}
                                                        />
                                                        &nbsp;{' '}
                                                        <span
                                                            style={
                                                                isMoveTypeAvailable(MoveType.NewApplication)
                                                                    ? styles.enabled
                                                                    : styles.disabled
                                                            }
                                                        >
                                                            Move Properties to a new Application (based on current Application)
                                                        </span>
                                                        {moveType === MoveType.NewApplication && (
                                                            <div className='columns small-12 large-3'>
                                                                <label htmlFor='bfiNumber'>
                                                                    BFI Number for new Appl. <span className='req'>*</span>
                                                                </label>
                                                                <BfiNumber
                                                                    id={'bfiNumber'}
                                                                    onChange={set_bfiNumber}
                                                                    value={bfiNumber}
                                                                    required={true}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={styles.optionDiv}>
                                                        <input
                                                            name='moveType'
                                                            id='moveType2'
                                                            value='2'
                                                            checked={moveType === MoveType.ExistingApplication}
                                                            type='radio'
                                                            onChange={(e) => set_moveType(parseInt(e.target.value))}
                                                            disabled={!isMoveTypeAvailable(MoveType.ExistingApplication)}
                                                        />
                                                        &nbsp;{' '}
                                                        <span
                                                            style={
                                                                isMoveTypeAvailable(MoveType.ExistingApplication)
                                                                    ? styles.enabled
                                                                    : styles.disabled
                                                            }
                                                        >
                                                            Move Properties to an another existing Application
                                                        </span>
                                                    </div>
                                                    <div style={styles.optionDiv}>
                                                        <input
                                                            name='moveType'
                                                            id='moveType0'
                                                            value='0'
                                                            checked={moveType === MoveType.Delete}
                                                            type='radio'
                                                            onChange={(e) => set_moveType(parseInt(e.target.value))}
                                                            disabled={!isMoveTypeAvailable(MoveType.Delete)}
                                                        />
                                                        &nbsp;{' '}
                                                        <span
                                                            style={
                                                                isMoveTypeAvailable(MoveType.Delete)
                                                                    ? styles.enabled
                                                                    : styles.disabled
                                                            }
                                                        >
                                                            Delete Properties from this Application
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-12'
                                                    style={styles.formColumn}
                                                >
                                                    <div style={styles.instructions}>
                                                        2. Select Properties{' '}
                                                        {moveType === MoveType.Delete ? 'to Delete' : 'to Move'}
                                                        ...
                                                    </div>
                                                    {(moveType === MoveType.Delete || moveType === MoveType.NewApplication) && (
                                                        <div style={styles.instructions2}>
                                                            At least 1 property must be checked and at least one property must be
                                                            left <b>unchecked</b>.
                                                        </div>
                                                    )}
                                                    {moveType === MoveType.ExistingApplication && (
                                                        <div style={styles.instructions2}>
                                                            At least 1 property must be checked.
                                                        </div>
                                                    )}
                                                    <div style={styles.propertyList}>
                                                        <List
                                                            id='propIds2'
                                                            items={application.properties}
                                                            selectedItemKeys={properties}
                                                            showSelectionControls={true}
                                                            itemRender={renderPropertyItem}
                                                            selectionMode='multiple'
                                                            height={200}
                                                            value={properties}
                                                            onOptionChanged={onPropertySelectionChanged}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {moveType === MoveType.ExistingApplication && (
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div className='columns small-12 large-12'>
                                                        <div style={styles.instructions}>
                                                            3. Select Target Application (click row to select)...
                                                        </div>
                                                        <div style={styles.instructions2}>
                                                            Only Applications of the same Type and Entity are available!
                                                        </div>
                                                        <div
                                                            style={styles.searchContainer}
                                                            className='input-has-icon'
                                                        >
                                                            <input
                                                                type='search'
                                                                placeholder='Search on BFI Number, City, State, Borrower or User...'
                                                                defaultValue={searchText}
                                                                onChange={_onSearchChange}
                                                                style={styles.searchBox}
                                                            />
                                                            <FontAwesomeIcon
                                                                icon={faSearch}
                                                                aria-hidden='true'
                                                                className='icon'
                                                            />
                                                        </div>
                                                        <BfiDataGrid
                                                            // gridType={GridSettingType.PrincipalApplications}
                                                            defaultPageSize={15}
                                                            dataStore={dataStore}
                                                            applicationInsights={applicationInsights}
                                                            noDataText='No Applications found'
                                                            onRowPrepared={onRowPrepared}
                                                            onSelectionChanged={onTargetAppSelected}
                                                        >
                                                            <Column
                                                                dataField={'loanTypeId'}
                                                                caption='Type'
                                                                width={100}
                                                                cellRender={onLoanTypeCellRender}
                                                                alignment='left'
                                                            />
                                                            <Column
                                                                dataField='borrowerEntity'
                                                                caption='Entity'
                                                                minWidth={120}
                                                                alignment='left'
                                                                cellRender={onBorrowerCellRender}
                                                            />
                                                            <Column
                                                                dataField='propertyCity'
                                                                caption='City'
                                                                width={60}
                                                                alignment='left'
                                                                cellRender={onCityCellRender}
                                                            />
                                                            <Column
                                                                dataField='propertyState'
                                                                caption='State'
                                                                width={60}
                                                                alignment='left'
                                                                cellRender={onStateCellRender}
                                                            />
                                                            <Column
                                                                dataField='bfiNumber'
                                                                caption='BFI #'
                                                                dataType='string'
                                                                width={100}
                                                            />
                                                            <Column
                                                                dataField='createdDate'
                                                                caption='Created'
                                                                width={100}
                                                                cellRender={onDateRender}
                                                                alignment='left'
                                                            />
                                                            <Column
                                                                dataField='appStatusId'
                                                                caption='Status'
                                                                dataType='string'
                                                                width={120}
                                                                cellRender={onAppStatusRender}
                                                            />
                                                            <MasterDetail
                                                                enabled={true}
                                                                render={renderPortfolioRecords}
                                                            />
                                                        </BfiDataGrid>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                            disabled={!canSubmit()}
                                        >
                                            Submit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <ConfirmModal
                isOpen={confirmModalOpen}
                confirmCallback={onConfirmTrue}
                cancelCallback={onConfirmFalse}
            >
                <div>
                    <i style={{ color: 'darkred' }}>
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            size='2x'
                        />
                    </i>
                    &nbsp;&nbsp;&nbsp;
                    <b>Warning</b>
                    <br />
                    <br />
                    {moveType === MoveType.Delete && (
                        <div>
                            This will:
                            <ul>
                                <li>
                                    Delete{' '}
                                    {properties.length === 1
                                        ? `${properties.length} property`
                                        : `${properties.length} properties`}{' '}
                                    from this application.
                                </li>
                                <li>Update HMDA records for this application.</li>
                            </ul>
                            Are you sure you want to perform these actions?
                        </div>
                    )}
                    {moveType === MoveType.NewApplication && (
                        <div>
                            This will:
                            <ul>
                                <li>
                                    Create a new application with the BFI #: <b>{bfiNumber}</b> duplicated from this application.
                                </li>
                                <li>
                                    Move{' '}
                                    {properties.length === 1
                                        ? `${properties.length} property`
                                        : `${properties.length} properties`}{' '}
                                    to the new application.
                                </li>
                                <li>Update HMDA records for this application.</li>
                                <li>Add a new HMDA record for the new application.</li>
                            </ul>
                            Are you sure you want to perform these actions?
                        </div>
                    )}
                    {moveType === MoveType.ExistingApplication && (
                        <div>
                            {properties.length === application.properties.length && (
                                <div>
                                    This will:
                                    <ul>
                                        <li>Move all properties to the selected application</li>
                                        <li>
                                            Change the status of this application to <b>Recycle Bin - Reassigned</b>.
                                        </li>
                                        <li>
                                            If any properties on the target application are in an Appraise state, any Submitted
                                            properties and the application will be moved to an Appraise state.
                                        </li>
                                        <li>Remove this application from HMDA records.</li>
                                        <li>Update HMDA records for the target application.</li>
                                    </ul>
                                </div>
                            )}
                            {properties.length < application.properties.length && (
                                <div>
                                    This will:
                                    <ul>
                                        <li>
                                            Move{' '}
                                            {properties.length === 1
                                                ? `${properties.length} property`
                                                : `${properties.length} properties`}{' '}
                                            to the selected application.
                                        </li>
                                        <li>
                                            If any properties on the target application are in an Appraise state, any Submitted
                                            properties and the application will be moved to Appraise state.
                                        </li>
                                        <li>Update HMDA records for both this application and the target application.</li>
                                    </ul>
                                </div>
                            )}
                            Are you sure you want to perform these actions?
                        </div>
                    )}
                </div>
            </ConfirmModal>
        </>
    );
};

export default MovePropertiesModal;
